import React, { useMemo, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../styles/contactus.css";

export default function ContactUs() {
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState("");

  const timeSlots = [
    "09:00 AM",
    "10:00 AM",
    "11:00 AM",
    "01:00 PM",
    "02:00 PM",
    "03:00 PM",
  ];

  const formattedDate = useMemo(
    () =>
      date.toLocaleDateString("en-CA", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
    [date]
  );

  const handleSubmit = (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    const formData = new FormData(form);

    const name = formData.get("name");
    const email = formData.get("email");
    const organization = formData.get("organization");
    const subjectArea = formData.get("subjectArea");
    const message = formData.get("message");

    const emailSubject = encodeURIComponent(
      `GESI Contact Request from ${name}`
    );

    const emailBody = encodeURIComponent(
      `Preferred Date: ${formattedDate}\n` +
        `Preferred Time: ${time}\n\n` +
        `Name: ${name}\n` +
        `Email: ${email}\n` +
        `Organization: ${organization || "Not provided"}\n` +
        `Area of Support: ${subjectArea || "Not selected"}\n\n` +
        `Message:\n${message || "No additional message provided."}`
    );

    const gmailUrl =
      "https://mail.google.com/mail/?view=cm&fs=1" +
      "&to=info@galaxyedgesafety.com " +
      `&su=${emailSubject}` +
      `&body=${emailBody}`;

    window.open(gmailUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <main className="contact-page">
      <section
        className="contact-section"
        aria-labelledby="contact-page-title"
      >
        <div className="contact-background" aria-hidden="true">
          <span className="contact-glow contact-glow-left"></span>
          <span className="contact-glow contact-glow-right"></span>
          <span className="contact-orbit contact-orbit-one"></span>
          <span className="contact-orbit contact-orbit-two"></span>
        </div>

        <div className="contact-page-inner">
          <header className="contact-page-header">
            <p className="contact-kicker">Connect With GESI</p>

            <h1 id="contact-page-title" className="contact-heading">
              Start a
              <span> Conversation</span>
            </h1>

            <p className="contact-subheading">
              Tell us about your organization, current priorities, and the
              support you are looking for. Select a preferred date and time,
              and our team will follow up to continue the conversation.
            </p>
          </header>

          <div className="contact-container">
            {/* CONTACT FORM */}
            <article className="contact-form-panel">
              <div className="contact-panel-heading">
                <div>
                  <p className="contact-panel-kicker">Your Request</p>
                  <h2>How can we help?</h2>
                </div>

                <span className="contact-panel-number" aria-hidden="true">
                  01
                </span>
              </div>

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="contact-form-row">
                  <div className="contact-field">
                    <label htmlFor="name">Name</label>

                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Your full name"
                      autoComplete="name"
                      required
                    />
                  </div>

                  <div className="contact-field">
                    <label htmlFor="email">Email</label>

                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>

                <div className="contact-form-row">
                  <div className="contact-field">
                    <label htmlFor="organization">
                      Organization
                      <span>Optional</span>
                    </label>

                    <input
                      id="organization"
                      name="organization"
                      type="text"
                      placeholder="Organization name"
                      autoComplete="organization"
                    />
                  </div>

                  <div className="contact-field">
                    <label htmlFor="subjectArea">
                      Area of support
                    </label>

                    <select
                      id="subjectArea"
                      name="subjectArea"
                      defaultValue=""
                    >
                      <option value="" disabled>
                        Select an area
                      </option>

                      <option value="OHS Support">
                        OHS Support
                      </option>

                      <option value="Audit and Gap Analysis">
                        Audit and Gap Analysis
                      </option>

                      <option value="Governance Advisory">
                        Governance Advisory
                      </option>

                      <option value="Training and Capability">
                        Training and Capability
                      </option>

                      <option value="Digital and AI Solutions">
                        Digital and AI Solutions
                      </option>

                      <option value="Partnerships and Initiatives">
                        Partnerships and Initiatives
                      </option>

                      <option value="Other">
                        Other
                      </option>
                    </select>
                  </div>
                </div>

                <div className="contact-field">
                  <label htmlFor="message">Message</label>

                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    placeholder="Tell us about your current challenge, priorities, or the type of support you are seeking."
                  />
                </div>

                <div
                  className="contact-selection-summary"
                  aria-live="polite"
                >
                  <div className="contact-selection-icon" aria-hidden="true">
                    ✓
                  </div>

                  <div>
                    <span>Preferred meeting</span>

                    <p>
                      {formattedDate}
                      {time ? ` at ${time}` : " — select a time"}
                    </p>
                  </div>
                </div>

                <button type="submit" className="contact-submit">
                  <span>Send Message</span>

                  <span
                    className="contact-submit-arrow"
                    aria-hidden="true"
                  >
                    ↗
                  </span>
                </button>

                <p className="contact-form-note">
                  Submitting this form opens a prepared email in Gmail so you
                  can review the message before sending it.
                </p>
              </form>
            </article>

            {/* SCHEDULING PANEL */}
            <aside
              className="contact-calendar-panel"
              aria-labelledby="contact-schedule-title"
            >
              <div className="contact-panel-heading">
                <div>
                  <p className="contact-panel-kicker">
                    Preferred Schedule
                  </p>

                  <h2 id="contact-schedule-title">
                    Select a date and time
                  </h2>
                </div>

                <span className="contact-panel-number" aria-hidden="true">
                  02
                </span>
              </div>

              <div className="contact-calendar">
                <Calendar
                  onChange={setDate}
                  value={date}
                  minDate={new Date()}
                  minDetail="month"
                  next2Label={null}
                  prev2Label={null}
                />
              </div>

              <div className="contact-selected-date">
                <span>Selected date</span>
                <strong>{formattedDate}</strong>
              </div>

              <fieldset className="contact-time-fieldset">
                <legend>Select a preferred time</legend>

                <div className="time-picker">
                  {timeSlots.map((slot) => (
                    <label
                      key={slot}
                      className={`time-option${
                        time === slot ? " selected" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="preferredTime"
                        value={slot}
                        checked={time === slot}
                        onChange={() => setTime(slot)}
                        required
                      />

                      <span>{slot}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="contact-calendar-note">
                <span aria-hidden="true">i</span>

                <p>
                  Your selected date and time are treated as a preference.
                  Final availability will be confirmed by the GESI team.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}